import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Plus as PlusIcon, Bike, Store, Gift, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import api, { money, apiError } from "@/lib/api";
import { findNearestOpenBranch } from "@/lib/branches";
import { toast } from "sonner";

export default function CartDrawer() {
  const { open, setOpen, items, updateQty, removeItem, totals, orderType, setOrderType,
    promoCode, setPromoCode, note, setNote, validationError, addItem,
    redeemPoints, setRedeemPoints, payload, clearCart,
    selectedBranch, setSelectedBranch, branchStatus, postcode } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const [recs, setRecs] = useState([]);
  const [promoInput, setPromoInput] = useState(promoCode);
  const [switching, setSwitching] = useState(false);
  const [reward, setReward] = useState(null);
  const navigate = useNavigate();

  const branchClosed = selectedBranch && branchStatus?.is_open === false;
  const switchToNearestOpen = async () => {
    setSwitching(true);
    const b = await findNearestOpenBranch({ postcode, excludeId: selectedBranch?.id });
    setSwitching(false);
    if (b) { setSelectedBranch({ id: b.id, name: b.name }); toast.success(`Switched to ${b.name.replace("METROS — ", "")}`); }
    else toast.error("No open branches nearby right now.");
  };

  const rw = settings?.rewards || {};
  const points = user?.loyalty_points || 0;
  const threshold = rw.reward_threshold || 0;
  const value = rw.reward_value || 0;
  const remaining = (totals?.subtotal || 0) + (orderType === "delivery" ? (totals?.delivery_fee || 0) : 0) - (totals?.discount || 0) - (totals?.combo_discount || 0);
  const maxBlocks = (user && rw.enabled && threshold > 0 && value > 0 && points >= threshold && remaining > 0)
    ? Math.max(0, Math.min(Math.floor(points / threshold), Math.floor(remaining / value))) : 0;
  const curBlocks = threshold ? Math.min(maxBlocks, Math.round(redeemPoints / threshold)) : 0;

  useEffect(() => {
    if (open) api.get("/menu/products?recommended=true").then(({ data }) => setRecs(data.slice(0, 4))).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (open && user) api.get(`/account/rewards${selectedBranch ? `?branch_id=${encodeURIComponent(selectedBranch.id)}` : ""}`).then(({ data }) => setReward(data?.order_reward || null)).catch(() => setReward(null));
  }, [open, user, selectedBranch]);

  const goCheckout = () => { setOpen(false); navigate("/checkout"); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} className="fixed inset-0 bg-black/50 z-[110]" data-testid="cart-overlay" />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-[120] flex flex-col shadow-cart"
            data-testid="cart-drawer">
            <div className="flex items-center justify-between gap-2 px-5 py-2.5 border-b border-gray-100">
              <h2 className="font-heading font-extrabold text-lg flex items-center gap-2 shrink-0">
                <ShoppingBag size={18} /> Your Basket
              </h2>
              <div className="grid grid-cols-2 gap-1 bg-gray-100 p-0.5 rounded-full flex-1 max-w-[220px]">
                {[["delivery", Bike, "Delivery"], ["collection", Store, "Collection"]].map(([v, Icon, label]) => (
                  <button key={v} data-testid={`cart-type-${v}`} onClick={() => setOrderType(v)}
                    className={`flex items-center justify-center gap-1.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${orderType === v ? "bg-metros-black text-white" : "text-gray-600"}`}>
                    <Icon size={14} /> {label}
                  </button>
                ))}
              </div>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="p-1 hover:text-metros-red shrink-0"><X size={20} /></button>
            </div>

            {user && reward?.enabled && reward.percent > 0 && (
              <div data-testid="cart-reward-countdown" className="mx-5 mt-3 rounded-xl bg-metros-black text-white px-3.5 py-3 flex items-center gap-2">
                <Gift size={16} className="text-metros-red shrink-0" />
                <p className="text-sm font-semibold">
                  {reward.is_next_reward || reward.until_reward === 0
                    ? <>Your <b className="text-metros-red">{reward.percent}% reward</b> is ready — applied automatically at checkout!</>
                    : reward.until_reward === 1
                      ? <><b className="text-metros-red">1 more order</b> for {reward.percent}% off your next one!</>
                      : reward.completed === 0
                        ? <><b className="text-metros-red">{reward.until_reward} orders</b> to your first {reward.percent}% reward 🎁</>
                        : <><b className="text-metros-red">{reward.until_reward} more orders</b> for {reward.percent}% off</>}
                </p>
              </div>
            )}

            {!user && settings?.order_reward?.enabled && settings.order_reward.percent > 0 && (
              <div data-testid="cart-signin-reward" className="mx-5 mt-3 rounded-xl bg-metros-black text-white px-3.5 py-3 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold flex items-center gap-2"><Gift size={16} className="text-metros-red shrink-0" /> Sign in to get {settings.order_reward.percent}% off your {settings.order_reward.every}th order</p>
                <button data-testid="cart-signin-btn" onClick={() => { setOpen(false); navigate("/login"); }} className="shrink-0 bg-metros-red hover:bg-metros-redhover text-white rounded-full px-4 py-1.5 text-xs font-bold active:scale-95 transition-all">Sign in</button>
              </div>
            )}

            {branchClosed && (
              <div data-testid="cart-branch-closed" className="mx-5 mt-3 rounded-xl border-2 border-amber-300 bg-amber-50 px-3.5 py-2.5">
                <p className="text-sm font-semibold text-amber-900 flex items-start gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                  <span>{selectedBranch.name.replace("METROS — ", "")} is closed right now{branchStatus?.opens_at ? ` · opens ${branchStatus.opens_at}` : ""}.</span>
                </p>
                <button data-testid="cart-switch-open" onClick={switchToNearestOpen} disabled={switching}
                  className="mt-1.5 ml-3.5 text-sm text-metros-red font-bold underline flex items-center gap-1 disabled:opacity-60">
                  {switching && <Loader2 className="animate-spin" size={13} />} Switch to nearest open branch
                </button>
              </div>
            )}

            <div className="flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center gap-3 py-16" data-testid="empty-cart">
                  <div className="bg-gray-100 rounded-full p-5"><ShoppingBag size={32} className="text-gray-400" /></div>
                  <p className="font-heading font-bold text-lg">Your basket is empty</p>
                  <p className="text-sm text-muted-foreground">Add some delicious food to get started.</p>
                  <button onClick={() => { setOpen(false); navigate("/menu"); }}
                    className="mt-2 bg-metros-red text-white rounded-full px-6 py-2.5 font-semibold">Browse Menu</button>
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {items.map((it) => {
                    const addons = it.meal_addons || [];
                    const optNames = (it.selected_options || []).map((o) => o.name);
                    const linePrice = ((it.price || 0) + (it.selected_options || []).reduce((s, o) => s + (o.price || 0), 0) + addons.reduce((s, a) => s + (a.price || 0), 0)) * it.quantity;
                    return (
                    <div key={it.uid} className="flex gap-3 border-b border-gray-50 pb-3" data-testid={`cart-item-${it.uid}`}>
                      {it.image ? (
                        <img src={it.image} alt={it.name} className="h-16 w-16 rounded-xl object-cover shrink-0" />
                      ) : (
                        <div className="h-16 w-16 rounded-xl bg-gray-50 flex items-center justify-center shrink-0"><img src="/logo.png" alt="METROS" className="h-9 w-9 object-contain opacity-60" /></div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <h4 className="font-semibold text-sm text-metros-black">{it.name}{addons.length > 0 && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-metros-red align-middle">Meal</span>}{it.reward_item_id && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-green-600 align-middle">Reward · {it.reward_points} pts</span>}</h4>
                          <button onClick={() => removeItem(it.uid)} data-testid={`remove-${it.uid}`} className="text-gray-400 hover:text-metros-red shrink-0"><Trash2 size={16} /></button>
                        </div>
                        {optNames.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{optNames.join(", ")}</p>
                        )}
                        {addons.length > 0 && (
                          <div className="mt-0.5 space-y-0.5">
                            {addons.map((a, idx) => (
                              <div key={idx} className="flex justify-between gap-2 text-xs text-muted-foreground" data-testid={`cart-addon-${it.uid}-${idx}`}>
                                <span className="truncate">+ {a.name}</span><span className="shrink-0">{money(a.price || 0)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {it.special_instructions && <p className="text-xs italic text-muted-foreground">“{it.special_instructions}”</p>}
                        <div className="flex justify-between items-center mt-1.5">
                          {it.reward_item_id ? (
                            <span className="text-xs font-semibold text-green-600">Reward · Qty 1</span>
                          ) : (
                            <div className="flex items-center gap-2 bg-gray-100 rounded-full px-1.5 py-1">
                              <button onClick={() => updateQty(it.uid, -1)} data-testid={`dec-${it.uid}`} className="p-0.5"><Minus size={14} /></button>
                              <span className="text-sm font-bold w-4 text-center">{it.quantity}</span>
                              <button onClick={() => updateQty(it.uid, 1)} data-testid={`inc-${it.uid}`} className="p-0.5"><Plus size={14} /></button>
                            </div>
                          )}
                          <span className="font-bold text-sm">{(it.reward_item_id && it.product_id) ? "Free" : money(linePrice)}</span>
                        </div>
                      </div>
                    </div>
                    );
                  })}

                  {recs.length > 0 && (
                    <div className="mt-3">
                      <h4 className="font-heading font-bold text-sm mb-2">Complete your order</h4>
                      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1">
                        {recs.map((r) => (
                          <div key={r.id} className="w-28 shrink-0 border border-gray-100 rounded-xl p-2" data-testid={`rec-${r.id}`}>
                            <img src={r.image} alt={r.name} className="h-16 w-full object-cover rounded-lg" />
                            <p className="text-xs font-semibold mt-1 line-clamp-1">{r.name}</p>
                            <div className="flex justify-between items-center mt-1">
                              <span className="text-xs font-bold">{money(r.price)}</span>
                              <button onClick={() => addItem({ product_id: r.id, name: r.name, image: r.image, price: r.price, quantity: 1, selected_options: [], special_instructions: "" })}
                                className="bg-gray-100 hover:bg-metros-red hover:text-white rounded-full p-1" data-testid={`rec-add-${r.id}`}><PlusIcon size={14} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 flex flex-col gap-2">
                    <input data-testid="cart-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note for the kitchen…"
                      className="w-full rounded-xl border border-gray-200 p-2.5 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
                    <div className="flex gap-2">
                      <input data-testid="promo-input" value={promoInput} onChange={(e) => setPromoInput(e.target.value)} placeholder="Promo code"
                        className="flex-1 rounded-xl border border-gray-200 p-2.5 text-sm uppercase focus:ring-2 focus:ring-metros-red focus:outline-none" />
                      <button data-testid="apply-promo" onClick={() => setPromoCode(promoInput)}
                        className="bg-metros-black text-white rounded-xl px-4 text-sm font-semibold">Apply</button>
                    </div>
                    {maxBlocks > 0 && (
                      <div data-testid="points-redeem" className="border border-gray-200 rounded-xl px-3 py-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-semibold flex items-center gap-1.5"><Gift size={15} className="text-metros-red" /> Reward points</span>
                          <span className="text-xs text-muted-foreground">You have {points}</span>
                        </div>
                        <input type="range" min="0" max={maxBlocks} step="1" value={curBlocks} data-testid="points-slider"
                          onChange={(e) => setRedeemPoints(Number(e.target.value) * threshold)}
                          className="w-full accent-metros-red" />
                        <div className="flex items-center justify-between text-xs mt-1">
                          <span className="text-muted-foreground">{curBlocks * threshold} pts</span>
                          <span className={`font-bold ${curBlocks > 0 ? "text-metros-red" : "text-gray-400"}`}>{curBlocks > 0 ? `−${money(curBlocks * value)}` : "£0.00 off"}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-gray-100 px-5 py-3">
                {validationError && <p className="text-sm text-metros-red mb-2" data-testid="cart-error">{validationError}</p>}
                {totals && (
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs text-muted-foreground mb-2.5" data-testid="cart-totals-summary">
                    <span>Subtotal {money(totals.subtotal)}</span>
                    {orderType === "delivery" && <span>· Delivery {totals.delivery_fee === 0 ? "FREE" : money(totals.delivery_fee)}</span>}
                    {totals.discount > 0 && <span className="text-metros-red">· Disc −{money(totals.discount)}</span>}
                    {totals.combo_discount > 0 && <span className="text-metros-red" data-testid="cart-meal-saving">· Meal −{money(totals.combo_discount)}</span>}
                    {totals.points_discount > 0 && <span className="text-metros-red">· Points −{money(totals.points_discount)}</span>}
                  </div>
                )}
                <button data-testid="checkout-btn" onClick={goCheckout} disabled={!totals}
                  className="w-full bg-metros-red hover:bg-metros-redhover text-white rounded-full py-3 font-bold active:scale-95 transition-all disabled:opacity-50 flex items-center justify-between px-5">
                  <span>Checkout</span>
                  <span className="font-heading font-extrabold">{totals ? money(totals.total) : ""}</span>
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
